import React, { useState, useMemo } from "react";
import { Canvas } from "@react-three/fiber";
import { OrbitControls, useGLTF } from "@react-three/drei";
import * as THREE from "three";

const muscleWorkouts: Record<string, string[]> = {
  'Chest': ["Bench Press", "Push-Ups", "Chest Fly"],
  'Traps': ["Shrugs"],
  'Shoulder Left': ["DB Shoulder Press"],
  'Shoulder Right': ["DB Shoulder Press"],
  'Tricep Left': ["Skull Crushers"],
  'Tricep Right': ["Skull Crushers"],
  'Bicep Left': ["DB Curls"],
  'Bicep Right': ["DB Curls"],
  'Lats': ["Pull Ups"],
  'Abs': ['STOP EATING SO MUCH'],
  'Glutes': ['Squats'],
  'Quads': ['Lunges'],
  'Hams': ['RDLs'],
  'Calves': ['Calf Raises']
};

interface MyProfileProps {
  profileData: {
    weight: string;
    height: string;
    benchPR: string;
    squatPR: string;
    deadliftPR: string;
  };
  setProfileData: React.Dispatch<React.SetStateAction<any>>;
  workoutRoutine: { name: string; reps: string; sets: string }[];
  setWorkoutRoutine: React.Dispatch<React.SetStateAction<{ name: string; reps: string; sets: string }[]>>;
}

interface HumanBodyProps {
  selectedMuscle: string | null;
  setSelectedMuscle: React.Dispatch<React.SetStateAction<string | null>>;
}

function Model({ url, onMuscleClick }: { url: string; onMuscleClick: (muscle: string) => void }) {
  const { scene } = useGLTF(url);
  const [hoveredMuscle, setHoveredMuscle] = useState<string | null>(null);

  const meshes = useMemo(() => {
    const clone = scene.clone(true);
    const extractedMeshes: THREE.Mesh[] = [];

    clone.traverse((child) => {
      if (child instanceof THREE.Mesh) {
        const mesh = child;
        mesh.userData.muscle =
          mesh.name.includes("Chest") ? "Chest" :
          mesh.name.includes("Traps") ? "Traps" :
          mesh.name.includes("Shoulder_Left") ? "Shoulder Left" :
          mesh.name.includes("Shoulder_Right001") ? "Shoulder Right" :
          mesh.name.includes("Tricep_Left") ? "Tricep Left" :
          mesh.name.includes("Tricep_Right") ? "Tricep Right" :
          mesh.name.includes("Bicep_Left") ? "Bicep Left" :
          mesh.name.includes("Bicep_Right") ? "Bicep Right" :
          mesh.name.includes("Lats") ? "Lats" :
          mesh.name.includes("Abs") ? "Abs" :
          mesh.name.includes("Glutes") ? "Glutes" :
          mesh.name.includes("Quads") ? "Quads" :
          mesh.name.includes("Hams") ? "Hams" :
          mesh.name.includes("Calves") ? "Calves" : null;

        // Clone material
        mesh.material = mesh.material.clone();

        // Store original color
        if (!mesh.userData.originalColor) {
          mesh.userData.originalColor = mesh.material.color.clone();
        }

        extractedMeshes.push(mesh);
      }
    });

    return extractedMeshes;
  }, [scene]);

  return (
    <group>
      {meshes.map((mesh, index) => (

        <mesh
          key={index}
          geometry={mesh.geometry}
          material={mesh.material}
          position={mesh.position}
          rotation={mesh.rotation}
          scale={mesh.scale}
          onPointerOver={(e) => {
            e.stopPropagation();
            const muscle = mesh.userData.muscle;
            const material = mesh.material as THREE.Material;
            if (muscle && "color" in material) {
              setHoveredMuscle(muscle);
              (material as THREE.MeshStandardMaterial).color.set("red");
              console.log(`Hovered: ${muscle}`);
            }
          }}
          
          onPointerOut={(e) => {
            e.stopPropagation();
            const muscle = mesh.userData.muscle;
            const material = mesh.material as THREE.Material;
            if (muscle && "color" in material && mesh.userData.originalColor) {
              setHoveredMuscle(null);
              (material as THREE.MeshStandardMaterial).color.copy(mesh.userData.originalColor);
            }
          }}
          
          onPointerDown={(e) => {
            e.stopPropagation();
            const muscle = mesh.userData.muscle;
            if (muscle) {
              console.log(`Clicked: ${muscle}`);
              onMuscleClick(muscle);
            }
          }}
        />
      ))}
    </group>
  );
}

const HumanBody: React.FC<HumanBodyProps> = ({
  selectedMuscle,
  setSelectedMuscle,
}) => {
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
      <Canvas camera={{ position: [0, 5, 10], fov: 50 }} style={{ width: "500px", height: "500px" }}>
        <ambientLight intensity={1} />
        <directionalLight position={[5, 5, 5]} intensity={2} />
        <Model url="/bodymusclegroups.glb" onMuscleClick={setSelectedMuscle} />
        <OrbitControls />
      </Canvas>
      {selectedMuscle && (
        <div style={{ marginTop: "20px", textAlign: "center" }}>
          <h2>Workouts for {selectedMuscle}</h2>
          <ul>
            {muscleWorkouts[selectedMuscle]?.map((workout) => (
              <li key={workout}>{workout}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

const MyProfile: React.FC<MyProfileProps> = ({
  profileData,
  setProfileData,
  workoutRoutine,
  setWorkoutRoutine,
}) => {
  const [workoutName, setWorkoutName] = useState("");
  const [reps, setReps] = useState("");
  const [sets, setSets] = useState("");

  const handleInputChange = (field: string, value: string) => {
    setProfileData((prev: any) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleAddWorkout = () => {
    if (workoutName && reps && sets) {
      setWorkoutRoutine([...workoutRoutine, { name: workoutName, reps, sets }]);
      setWorkoutName("");
      setReps("");
      setSets("");
    }
  };

  return (
    <div className="flex flex-col md:flex-row gap-6 text-white">
      <div className="space-y-4 w-full md:w-1/2">
        <h2 className="text-xl font-semibold">Personal Info</h2>
        <input
          type="text"
          placeholder="Body Weight (lbs)"
          value={profileData.weight}
          onChange={(e) => handleInputChange("weight", e.target.value)}
          className="w-full p-2 rounded bg-gray-700"
        />
        <input
          type="text"
          placeholder="Height (inches)"
          value={profileData.height}
          onChange={(e) => handleInputChange("height", e.target.value)}
          className="w-full p-2 rounded bg-gray-700"
        />
        <input
          type="text"
          placeholder="Bench PR (lbs)"
          value={profileData.benchPR}
          onChange={(e) => handleInputChange("benchPR", e.target.value)}
          className="w-full p-2 rounded bg-gray-700"
        />
        <input
          type="text"
          placeholder="Squat PR (lbs)"
          value={profileData.squatPR}
          onChange={(e) => handleInputChange("squatPR", e.target.value)}
          className="w-full p-2 rounded bg-gray-700"
        />
        <input
          type="text"
          placeholder="Deadlift PR (lbs)"
          value={profileData.deadliftPR}
          onChange={(e) => handleInputChange("deadliftPR", e.target.value)}
          className="w-full p-2 rounded bg-gray-700"
        />

        <h2 className="text-xl font-semibold pt-6">Add Workout</h2>
        <div className="flex gap-2">
          <input
            type="text"
            placeholder="Workout Name"
            value={workoutName}
            onChange={(e) => setWorkoutName(e.target.value)}
            className="flex-1 p-2 rounded bg-gray-700"
          />
          <input
            type="text"
            placeholder="Reps"
            value={reps}
            onChange={(e) => setReps(e.target.value)}
            className="w-20 p-2 rounded bg-gray-700"
          />
          <input
            type="text"
            placeholder="Sets"
            value={sets}
            onChange={(e) => setSets(e.target.value)}
            className="w-20 p-2 rounded bg-gray-700"
          />
          <button
            onClick={handleAddWorkout}
            className="bg-blue-600 px-4 rounded hover:bg-blue-500"
          >
            Add
          </button>
        </div>
      </div>

      <div className="bg-gray-800 rounded-lg p-4 w-full md:w-1/2 shadow-lg">
        <h2 className="text-xl font-bold mb-4">Your Profile Summary</h2>
        <p><strong>Weight:</strong> {profileData.weight} lbs</p>
        <p><strong>Height:</strong> {profileData.height} in</p>
        <p><strong>Bench PR:</strong> {profileData.benchPR} lbs</p>
        <p><strong>Squat PR:</strong> {profileData.squatPR} lbs</p>
        <p><strong>Deadlift PR:</strong> {profileData.deadliftPR} lbs</p>

        <h3 className="mt-6 text-lg font-semibold">Workout Routine</h3>
        <ul className="list-disc pl-5">
          {workoutRoutine.map((workout, index) => (
            <li key={index}>
              {workout.name} - {workout.sets} sets x {workout.reps} reps
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

const App: React.FC = () => {
  const [activeTab, setActiveTab] = useState("Workouts");
  const [selectedMuscle, setSelectedMuscle] = useState<string | null>(null);

  const [profileData, setProfileData] = useState({
    weight: "",
    height: "",
    benchPR: "",
    squatPR: "",
    deadliftPR: "",
  });
  
  const [workoutRoutine, setWorkoutRoutine] = useState<
    { name: string; reps: string; sets: string }[]
  >([]);
  

  const renderTabContent = () => {
    switch (activeTab) {
      case "My Profile":
        return (
          <MyProfile
            profileData={profileData}
            setProfileData={setProfileData}
            workoutRoutine={workoutRoutine}
            setWorkoutRoutine={setWorkoutRoutine}
          />
        );
      case "Workout Ideas":
        return (
          <HumanBody
            selectedMuscle={selectedMuscle}
            setSelectedMuscle={setSelectedMuscle}
          />
        );
      case "AI Stuff (In development)":
        return <div className="p-4 text-white">AI stuff currently being developed.</div>;
      case "Tab1":
        return <div className="p-4 text-white">Content for Tab 1</div>;
      case "Tab2":
        return <div className="p-4 text-white">Content for Tab 2</div>;
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      <div className="flex p-4 space-x-4 bg-gray-800">
        {["My Profile", "Workout Ideas", "AI Stuff (In development)", "Tab1", "Tab2"].map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-2 rounded ${
              activeTab === tab ? "bg-blue-600" : "bg-gray-700 hover:bg-gray-600"
            }`}
          >
            {tab}
          </button>
        ))}
      </div>
      <div className="p-4">{renderTabContent()}</div>
    </div>
  );
};

export default App;